'use client'

import { Button } from '@/components/ui/button'
import { getDetailUrl, getImagePreview, getWaveformPreview, isSoundsCrateSound } from '@/lib/assets/assets'
import { Asset, AssetFile, AssetSummary, SoundsAsset } from '@/lib/assets/types'
import { MarketplaceAssetPurchaseContextItem } from '@/lib/marketplace/types'
import { formatMarketplacePrice, getMarketplaceDisplayPrice } from '@/lib/marketplace/utils'
import {
  getCrateCartAssetLineKey,
  hasCrateCartLine,
  useCrateCartSnapshot,
  waitForCrateCart,
} from '@/lib/marketplace/crate-cart'
import { forDetails } from '@/lib/assets/repositories/asset-repository-factory'
import { cn, getEnvironment } from '@/lib/utils'
import { canShowMarketplaceAssetCartAction, isFreeAsset } from '@/lib/marketplace/asset-action-visibility'
import { storeCurrentMarketplaceCheckoutReturnUrl } from '@/lib/marketplace/checkout-return-url'
import { useUserContext } from '@/lib/contexts/UserContext'
import { useQuery } from '@tanstack/react-query'
import { CheckIcon, PlusIcon, ShoppingCartIcon } from 'lucide-react'
import { createPortal } from 'react-dom'
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useHeaderFeatures } from '@/lib/contexts/HeaderFeaturesContext'
import tooltipStyles from './marketplace-asset-purchase-button.module.scss'

interface MarketplaceAssetPurchaseButtonProps {
  asset: Asset | AssetSummary
  context?: MarketplaceAssetPurchaseContextItem | null
  file?: Pick<AssetFile, 'level'> | null
  className?: string
  compact?: boolean
  compactLayout?: 'stacked' | 'inline'
  compactVariant?: 'icon' | 'cta' | 'card' | 'tooltip'
  fallback?: ReactNode
}

export function MarketplaceAssetPurchaseButton(props: MarketplaceAssetPurchaseButtonProps) {
  const { marketplaceCartEnabled } = useHeaderFeatures()

  if (!marketplaceCartEnabled) {
    return props.fallback ? <>{props.fallback}</> : null
  }

  return <MarketplaceAssetPurchaseButtonContent {...props} />
}

function MarketplaceAssetPurchaseButtonContent({
  asset,
  context,
  file,
  className,
  compact = false,
  compactLayout = 'stacked',
  compactVariant = 'icon',
  fallback,
}: MarketplaceAssetPurchaseButtonProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const cardTooltipId = useId()
  const cardButtonRef = useRef<HTMLButtonElement>(null)
  const cardTooltipEnterFrameRef = useRef<number | null>(null)
  const cardTooltipHideTimeoutRef = useRef<number | null>(null)
  const [isCardTooltipMounted, setIsCardTooltipMounted] = useState(false)
  const [isCardTooltipVisible, setIsCardTooltipVisible] = useState(false)
  const [cardTooltipPosition, setCardTooltipPosition] = useState<{ top: number; left: number } | null>(null)
  const cartSnapshot = useCrateCartSnapshot()
  const { hasProFeature } = useUserContext()
  const hasInlineWaveformPreview = Boolean(getWaveformPreview(asset))
  const shouldResolveSoundDetails = isSoundsCrateSound(asset) && !hasInlineWaveformPreview && !!asset.mainCategorySlug

  const { data: soundAssetDetails } = useQuery({
    queryKey: ['marketplace-cart-sound-asset', asset.uuid],
    queryFn: async () => {
      const repository = forDetails()
      return (await repository.getAssetByIdOrSlug({
        assetIdOrSlug: asset.uuid,
        type: 'sc_sounds',
        mainCategoryIdOrSlug: asset.mainCategorySlug!,
      })) as SoundsAsset
    },
    enabled: shouldResolveSoundDetails,
    staleTime: 30 * 1000,
  })

  const resolvedAsset = soundAssetDetails ?? asset

  const assetUuid = resolvedAsset.uuid
  const defaultPrice = context?.default_price ?? null
  const priceLabel = getMarketplaceDisplayPrice(defaultPrice)
  const isInCart = assetUuid ? hasCrateCartLine(cartSnapshot, getCrateCartAssetLineKey(assetUuid)) : false
  const userCanDownloadPremiumAssets = hasProFeature('assets-download')
  const canShowCartAction = canShowMarketplaceAssetCartAction({
    asset,
    context,
    file,
    isInCart,
    userCanDownloadPremiumAssets,
  })
  const canAddToCart = Boolean(
    assetUuid && !isFreeAsset(asset) && context?.purchasable && context?.can_add_to_cart && !context?.owned && !isInCart
  )

  const assetPriceId =
    defaultPrice && ['asset_price', 'vertical_asset_price'].includes(defaultPrice.source_type)
      ? defaultPrice.source_id
      : undefined
  const previewUrl = isSoundsCrateSound(resolvedAsset) ? null : getImagePreview(resolvedAsset)
  const waveformUrl = getWaveformPreview(resolvedAsset)
  const assetPriceSourceType =
    defaultPrice?.source_type === 'asset_price' || defaultPrice?.source_type === 'vertical_asset_price'
      ? defaultPrice.source_type
      : undefined
  const cartVertical = isSoundsCrateSound(resolvedAsset) ? 'SFX' : undefined

  const addAssetToCart = async () => {
    if (!assetUuid || !canAddToCart || isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      const cart = await waitForCrateCart()
      const payload = {
        type: 'asset',
        assetUuid,
        assetHref: getDetailUrl(resolvedAsset),
        assetPriceId,
        assetPriceSourceType,
        display: {
          name: resolvedAsset.name,
          amount: defaultPrice?.amount ?? 0,
          currency: defaultPrice?.currency ?? 'usd',
          priceLabel: priceLabel ?? formatMarketplacePrice(defaultPrice?.amount ?? 0, defaultPrice?.currency ?? 'usd'),
          typeLabel: 'Asset',
          vertical: cartVertical,
          previewUrl,
          waveformUrl,
        },
        sourceContext: {
          explicitPurchase: true,
          originUrl: window.location.href,
          originDomain: window.location.hostname,
        },
      } as const

      if (getEnvironment() === 'qa') {
        console.debug('[marketplace] add asset to shared cart payload', payload)
      }

      await cart.addItem(payload)
      storeCurrentMarketplaceCheckoutReturnUrl()
    } catch (error) {
      console.error('[marketplace] failed to add asset to shared cart', error)
      toast.error('Cart is temporarily unavailable. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const updateCardTooltipPosition = useCallback(() => {
    const button = cardButtonRef.current
    if (!button) {
      return
    }

    const { top, left, width } = button.getBoundingClientRect()
    setCardTooltipPosition({ top, left: left + width / 2 })
  }, [])

  useEffect(() => {
    if (!isCardTooltipVisible) {
      return
    }

    updateCardTooltipPosition()
    window.addEventListener('resize', updateCardTooltipPosition)
    window.addEventListener('scroll', updateCardTooltipPosition, true)

    return () => {
      window.removeEventListener('resize', updateCardTooltipPosition)
      window.removeEventListener('scroll', updateCardTooltipPosition, true)
    }
  }, [isCardTooltipVisible, updateCardTooltipPosition])

  useEffect(() => {
    return () => {
      if (cardTooltipEnterFrameRef.current !== null) {
        window.cancelAnimationFrame(cardTooltipEnterFrameRef.current)
      }
      if (cardTooltipHideTimeoutRef.current !== null) {
        window.clearTimeout(cardTooltipHideTimeoutRef.current)
      }
    }
  }, [])

  if (!canShowCartAction) {
    return fallback ? <>{fallback}</> : null
  }

  const compactPrice =
    priceLabel && compactVariant !== 'tooltip' ? (
      <span
        className={cn(
          'tw-whitespace-nowrap tw-leading-none',
          compactVariant === 'cta'
            ? 'tw-text-3xl tw-text-white'
            : compactVariant === 'card'
              ? 'tw-text-[21px] tw-text-white'
              : 'tw-text-white/70',
          compactVariant === 'cta' || compactVariant === 'card' ? 'tw-font-medium' : 'tw-font-semibold',
          compactVariant !== 'cta' &&
            compactVariant !== 'card' &&
            (compactLayout === 'inline' ? 'tw-text-sm' : 'tw-text-[10px]')
        )}
      >
        {priceLabel}
      </span>
    ) : null

  const compactContainerClassName = cn(
    'tw-flex tw-items-center tw-justify-center',
    compactLayout === 'inline'
      ? cn('tw-flex-row', compactVariant === 'cta' ? 'tw-gap-4' : 'tw-gap-2')
      : 'tw-flex-col tw-gap-1',
    className
  )

  const compactButtonClassName = cn(
    compactVariant === 'cta'
      ? '!tw-w-[151px] !tw-rounded-full !tw-bg-white !tw-bg-none !tw-px-8 !tw-py-3 !tw-text-[var(--Primary)] hover:!tw-bg-white/90'
      : compactVariant === 'card'
        ? '!tw-h-[45px] !tw-w-[45px] !tw-rounded-full !tw-bg-black/50 !tw-bg-none !tw-p-0 !tw-text-white tw-shadow-lg tw-backdrop-blur-sm hover:!tw-bg-black/70'
        : compactVariant === 'tooltip'
          ? '!tw-rounded-full !tw-bg-white !tw-bg-none !tw-px-4 !tw-py-2 !tw-text-[var(--Primary)] hover:!tw-bg-white/90'
          : "tw-relative -tw-m-1 before:tw-absolute before:tw-content-['']",
    compactVariant !== 'cta' &&
      compactVariant !== 'card' &&
      compactVariant !== 'tooltip' &&
      (compactLayout === 'inline' ? '!tw-p-3 before:-tw-inset-2' : '!tw-p-2 before:-tw-inset-1')
  )
  const isCtaCompact = compact && compactVariant === 'cta'
  const isCardCompact = compact && compactVariant === 'card'
  const isTooltipCompact = compact && compactVariant === 'tooltip'

  const showCardTooltip = () => {
    if (cardTooltipHideTimeoutRef.current !== null) {
      window.clearTimeout(cardTooltipHideTimeoutRef.current)
      cardTooltipHideTimeoutRef.current = null
    }

    updateCardTooltipPosition()
    setIsCardTooltipMounted(true)
    if (cardTooltipEnterFrameRef.current !== null) {
      window.cancelAnimationFrame(cardTooltipEnterFrameRef.current)
    }
    cardTooltipEnterFrameRef.current = window.requestAnimationFrame(() => {
      setIsCardTooltipVisible(true)
      cardTooltipEnterFrameRef.current = null
    })
  }

  const hideCardTooltip = () => {
    if (cardTooltipEnterFrameRef.current !== null) {
      window.cancelAnimationFrame(cardTooltipEnterFrameRef.current)
      cardTooltipEnterFrameRef.current = null
    }

    setIsCardTooltipVisible(false)
    if (cardTooltipHideTimeoutRef.current !== null) {
      window.clearTimeout(cardTooltipHideTimeoutRef.current)
    }
    cardTooltipHideTimeoutRef.current = window.setTimeout(() => {
      setIsCardTooltipMounted(false)
      setCardTooltipPosition(null)
      cardTooltipHideTimeoutRef.current = null
    }, 150)
  }

  const cardTooltip = (label: string) => {
    if (!isCardTooltipMounted || !cardTooltipPosition || typeof document === 'undefined') {
      return null
    }

    return createPortal(
      <span
        style={{ top: cardTooltipPosition.top, left: cardTooltipPosition.left }}
        className="tw-pointer-events-none tw-fixed tw-z-[100] -tw-translate-x-1/2 -tw-translate-y-full"
      >
        <span
          id={cardTooltipId}
          role="tooltip"
          className={cn(
            'tw-relative tw-block tw-whitespace-nowrap tw-rounded-lg tw-bg-[rgb(19_29_39_/_0.94)] tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-xl',
            tooltipStyles.tooltipMotion,
            isCardTooltipVisible ? tooltipStyles.tooltipVisible : tooltipStyles.tooltipHidden
          )}
        >
          {label}
        </span>
      </span>,
      document.body
    )
  }

  if (isInCart) {
    if (compact) {
      return (
        <div className={compactContainerClassName}>
          {compactLayout === 'inline' ? compactPrice : null}
          {isCardCompact ? (
            <div
              className="tw-relative tw-flex"
              onMouseEnter={showCardTooltip}
              onMouseLeave={hideCardTooltip}
              onFocus={showCardTooltip}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  hideCardTooltip()
                }
              }}
            >
              {cardTooltip('Already in cart')}
              <Button
                ref={cardButtonRef}
                type="button"
                variant="transparent"
                size="icon"
                className={compactButtonClassName}
                aria-label="Already in cart"
                aria-describedby={cardTooltipId}
                disabled
              >
                <ShoppingCartIcon className="tw-h-[22px] tw-w-[22px] tw-text-white" />
                <CheckIcon className="tw-absolute -tw-right-1 -tw-top-1 tw-h-3 tw-w-3 tw-rounded-full tw-bg-[var(--Primary-100)] tw-p-[1px] tw-text-black" />
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="transparent"
              size={isCtaCompact || isTooltipCompact ? 'default' : 'icon'}
              className={compactButtonClassName}
              aria-label="Already in cart"
              title="Already in cart"
              disabled
            >
              {isCtaCompact ? (
                <span>In Cart</span>
              ) : isTooltipCompact ? (
                <>
                  <ShoppingCartIcon className="tw-mr-2 tw-h-4 tw-w-4" />
                  <span>In Cart</span>
                </>
              ) : (
                <>
                  <ShoppingCartIcon className="tw-h-4 tw-w-4 tw-text-white" />
                  <CheckIcon className="tw-absolute -tw-right-1 -tw-top-1 tw-h-3 tw-w-3 tw-rounded-full tw-bg-[var(--Primary-100)] tw-p-[1px] tw-text-black" />
                </>
              )}
            </Button>
          )}
          {compactLayout === 'stacked' ? compactPrice : null}
        </div>
      )
    }

    return (
      <div className={cn('tw-flex tw-items-center tw-gap-2', className)}>
        <Button type="button" size={compact ? 'sm' : 'default'} className="!tw-whitespace-nowrap" disabled>
          <ShoppingCartIcon className="tw-mr-2 tw-h-4 tw-w-4" />
          <span>In cart</span>
          {priceLabel ? <span className="tw-ml-3 tw-text-xs tw-opacity-80">{priceLabel}</span> : null}
        </Button>
      </div>
    )
  }

  if (compact) {
    return (
      <div className={compactContainerClassName}>
        {compactLayout === 'inline' ? compactPrice : null}
        {isCardCompact ? (
          <div
            className="tw-relative tw-flex"
            onMouseEnter={showCardTooltip}
            onMouseLeave={hideCardTooltip}
            onFocus={showCardTooltip}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                hideCardTooltip()
              }
            }}
          >
            {cardTooltip('Add to cart')}
            <Button
              ref={cardButtonRef}
              type="button"
              variant="transparent"
              size="icon"
              className={compactButtonClassName}
              aria-label="Add to cart"
              aria-describedby={cardTooltipId}
              disabled={!canAddToCart || isSubmitting}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void addAssetToCart()
              }}
            >
              <ShoppingCartIcon className="tw-h-[22px] tw-w-[22px] tw-text-white" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="transparent"
            size={isCtaCompact || isTooltipCompact ? 'default' : 'icon'}
            className={compactButtonClassName}
            aria-label="Add to cart"
            title="Add to cart"
            disabled={!canAddToCart || isSubmitting}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              void addAssetToCart()
            }}
          >
            {isCtaCompact ? (
              <span>{isSubmitting ? 'Adding...' : 'Add to Cart'}</span>
            ) : isTooltipCompact ? (
              <>
                <ShoppingCartIcon className="tw-mr-2 tw-h-4 tw-w-4" />
                <span>{isSubmitting ? 'Adding...' : 'Add to Cart'}</span>
              </>
            ) : (
              <>
                <ShoppingCartIcon className="tw-h-4 tw-w-4" />
                <PlusIcon className="tw-absolute -tw-right-1 -tw-top-1 tw-h-3 tw-w-3 tw-rounded-full tw-bg-[var(--Primary-100)] tw-p-[1px] tw-text-black" />
              </>
            )}
          </Button>
        )}
        {compactLayout === 'stacked' ? compactPrice : null}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'tw-flex tw-items-center tw-gap-2',
        compact ? 'tw-flex-col tw-items-start' : 'tw-flex-row',
        className
      )}
    >
      <Button
        type="button"
        size={compact ? 'sm' : 'default'}
        className={cn(compact ? 'tw-w-full' : '', '!tw-whitespace-nowrap')}
        disabled={!canAddToCart || isSubmitting}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void addAssetToCart()
        }}
      >
        <ShoppingCartIcon className="tw-mr-2 tw-h-4 tw-w-4" />
        <span>Add to cart</span>
        {priceLabel ? <span className="tw-ml-3 tw-text-xs tw-opacity-80">{priceLabel}</span> : null}
      </Button>
    </div>
  )
}
